import React, { useState, useEffect } from 'react'
import moment from 'moment'
import 'moment/locale/fi'
import IconButton from '@material-ui/core/IconButton'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import { useTranslation } from 'react-i18next'
import withAppconfig from '../AppContext'
import { useVenueResources } from '../utils/useVenueResources'
import { withFreeTimes } from './HomeScreenCalendar'
import ReadOnlyView from '../components/ReadOnlyView'
import { formatVacancies, getOffset, multipleConfigs, resolveInitialDay } from '../components/ViewUtils'

const APPINITLANG = 'fi'
const TIMEZONEOFFSET = getOffset('Europe/Helsinki')

// Embeddable read-only availability screen (/embed). No navigation bars; meant to
// be shown inside an iframe on third-party sites. Clicking a slot navigates the
// TOP-LEVEL page to the real homepage with the category preselected via ?_rcat=.
function ReadOnlyScreen({ appconfig, freeindex }) {
  const { t } = useTranslation()

  const now = moment.utc().add(Math.abs(TIMEZONEOFFSET), 'minutes')
  const today = appconfig.openingdate
    ? moment.utc(appconfig.openingdate).isAfter(now, 'd')
      ? moment.utc(appconfig.openingdate).add(Math.abs(TIMEZONEOFFSET), 'minutes').format('YYYY-MM-DD')
      : now.format('YYYY-MM-DD')
    : now.format('YYYY-MM-DD')
  const daysahead = (appconfig && appconfig.daysahead) || 13

  // initial state from URL params; the query is intentionally never stripped (?aff etc.)
  const params = new URLSearchParams(window.location.search)
  // ?width= lets embedding sites match the iframe width (px, clamped to 280-1600)
  const paramWidth = parseInt(params.get('width'), 10)
  const bodyWidth = paramWidth >= 280 && paramWidth <= 1600 ? paramWidth : 900
  const initialDay = resolveInitialDay(params.get('day'), today, daysahead)

  const [selectedday, setSelectedday] = useState(initialDay)
  const [handleDate, setHandleDate] = useState(moment.utc(initialDay, 'YYYY-MM-DD').format('YYYYMMDD'))
  const [vacancies, setVacancies] = useState({})
  const [cachedDates, setCachedDates] = useState([])
  const [cacheLoading, setCacheLoading] = useState(false)
  // The full list from getResources, null until it arrives (utils/useVenueResources.js).
  const { resources } = useVenueResources({ appconfig })

  useEffect(() => {
    if (selectedday && moment.utc(selectedday, 'YYYY-MM-DD').format('YYYYMMDD') !== handleDate) {
      setHandleDate(moment.utc(selectedday, 'YYYY-MM-DD').format('YYYYMMDD'))
    }
    if (freeindex && freeindex[handleDate] && resources) {
      const { key } = freeindex[handleDate]
      if (cachedDates.includes(key)) return
      setCacheLoading(true)
      fetch(key)
        .then((res) => res.json())
        .then((result) => {
          if (result && Object.keys(result).length > 0) {
            const { cindex } = formatVacancies(
              result,
              handleDate,
              resources,
              false,
              multipleConfigs(resources, appconfig.multipleconfig)
            )
            setVacancies((v) => ({ ...v, [handleDate]: cindex }))
            setCachedDates((c) => [...c, key])
          } else if (result && Object.keys(result).length === 0) {
            setVacancies((v) => ({ ...v, [handleDate]: [] }))
            setCachedDates((c) => [...c, key])
          }
          setCacheLoading(false)
        })
        .catch(() => setCacheLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDate, selectedday, freeindex, appconfig, resources])

  const moveDate = (days) => {
    let date
    if (days > 0) {
      date = moment.utc(selectedday).add(days, 'days')
    } else if (days < 0) {
      date = moment.utc(selectedday).subtract(-days, 'days')
    } else {
      date = moment.utc()
    }
    if (
      date.isValid() &&
      date.diff(moment.utc(), 'days') < daysahead &&
      date.diff(moment.utc(), 'days') > -1
    ) {
      setSelectedday(date.format('YYYY-MM-DD'))
      setHandleDate(date.format('YYYYMMDD'))
    }
  }

  const isFirstDay = selectedday === today
  const isLastDay = moment.utc(selectedday).diff(moment.utc(today), 'days') >= daysahead - 1

  const handleSlotClick = (category) => {
    const base =
      'https://' + (appconfig.istest ? 'utest.cintoia.com' + appconfig.pathPrefix : appconfig.hosts[0])
    const url = category ? base + '/?_rcat=' + encodeURIComponent(category) : base + '/'
    try {
      window.top.location.href = url
    } catch (e) {
      window.location.href = url
    }
  }

  let _weekday = moment.utc(selectedday).locale(APPINITLANG.toLowerCase()).format('dd')
  const dateLabel =
    _weekday.charAt(0).toUpperCase() + _weekday.slice(1) + ' ' + moment.utc(selectedday).format('D.M.YYYY')

  return (
    <div style={{ maxWidth: bodyWidth, minWidth: 'fit-content', margin: '0 auto', padding: 8 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          // stays visible while scrolling; category header sticks right below (top: 40)
          position: 'sticky',
          top: 0,
          height: 40,
          background: '#fff',
          zIndex: 3,
        }}
      >
        <IconButton size='small' onClick={() => moveDate(-1)} disabled={isFirstDay}>
          <KeyboardArrowLeftIcon />
        </IconButton>
        <span style={{ margin: '0 10px', fontWeight: 'bold' }}>{dateLabel}</span>
        <IconButton size='small' onClick={() => moveDate(1)} disabled={isLastDay}>
          <KeyboardArrowRightIcon />
        </IconButton>
      </div>
      <ReadOnlyView
        dailyFreeTimes={vacancies[handleDate] || []}
        appconfig={appconfig}
        onSlotClick={handleSlotClick}
        loading={cacheLoading || !freeindex || Boolean(freeindex[handleDate] && !vacancies[handleDate])}
        t={t}
      />
    </div>
  )
}

export default withAppconfig(withFreeTimes(ReadOnlyScreen))
